import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy | Acadly" },
      {
        name: "description",
        content:
          "Read how Acadly collects, uses and protects reader information, including details on cookies and analytics.",
      },
      { property: "og:title", content: "Privacy Policy | Acadly" },
      {
        property: "og:description",
        content:
          "Full privacy notice covering data collected on Acadly, cookie use, analytics and reader rights.",
      },
      { property: "og:url", content: "https://acadly.in/privacy-policy" },
    ],
    links: [{ rel: "canonical", href: "https://acadly.in/privacy-policy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="mx-auto max-w-3xl px-5 py-16 md:py-20 prose-post">
      <div className="stamp mb-6">Legal · Privacy</div>
      <h1
        className="text-4xl md:text-5xl font-semibold"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Privacy Policy
      </h1>
      <p className="mono-label !mt-2">Last updated: 25 July 2026</p>

      <p>
        This Privacy Policy sets out how <strong>Acadly.in</strong> (referred to
        in this notice as "Acadly", "we", "us" or "our") handles information
        about visitors to the website at{" "}
        <a href="https://acadly.in">acadly.in</a> (the "Site"). Please read it
        carefully. By continuing to use the Site you confirm that you have read
        and understood this policy. It is written in plain language for
        readers, and is intended to be transparent rather than to serve as
        legal advice.
      </p>

      <h2>1. Who runs the Site</h2>
      <p>
        Acadly is an independent editorial project operated by Dhairya from
        New Delhi, India. For any question relating to your personal data or
        this policy, please write to{" "}
        <a href="mailto:hello@acadly.in">hello@acadly.in</a>.
      </p>

      <h2>2. Information we collect</h2>
      <p>
        Acadly is a static content website. It does not require an account, and
        most visitors can read every article without submitting any information
        at all. The information we do handle falls into three categories.
      </p>

      <h3>2.1 Information you provide directly</h3>
      <ul>
        <li>
          <strong>Contact form and email:</strong> when you write to us through
          the contact page or by email, we receive your name, email address
          and the contents of your message. The current contact form composes
          the message in your own email client and sends it from your inbox to
          ours; it does not store submissions on our servers.
        </li>
        <li>
          <strong>Email updates:</strong> if an email subscription is offered
          on the Site and you choose to sign up, we store the email address
          you provide so we can send new articles to it. Every message
          includes a one-click unsubscribe link.
        </li>
      </ul>

      <h3>2.2 Information collected automatically</h3>
      <ul>
        <li>
          <strong>Log data:</strong> when you load a page, our hosting provider
          logs standard request information such as your IP address, browser
          type and version, operating system, the page requested, the
          referring page and a timestamp. This data is used to keep the Site
          reliable and secure.
        </li>
        <li>
          <strong>Usage data:</strong> aggregated, non-identifying signals
          such as which articles are read, how long a page stays open and
          which device categories are used. This helps us decide what to
          write next.
        </li>
      </ul>

      <h3>2.3 Cookies and similar technologies</h3>
      <p>
        The Site uses cookies and comparable technologies as described in the
        Cookies section below.
      </p>

      <h2>3. How we use information</h2>
      <p>We use the information listed above to:</p>
      <ul>
        <li>Deliver, maintain and secure the Site.</li>
        <li>Reply to messages you send us and act on corrections you report.</li>
        <li>
          Understand which articles are useful in aggregate, so we can improve
          the writing.
        </li>
        <li>
          Detect abuse of the Site, prevent fraud and comply with legal
          obligations.
        </li>
      </ul>
      <p>
        We do not sell your personal information, and we do not share it with
        third parties for their own marketing purposes.
      </p>

      <h2>4. Cookies</h2>
      <p>
        A cookie is a small text file stored on your device by your browser.
        The Site uses cookies for basic functionality, analytics, and advertising as set out below.
      </p>
      <ul>
        <li>
          <strong>Strictly necessary:</strong> required for the Site to work
          and to remember basic preferences such as your cookie choice, where
          applicable.
        </li>
        <li>
          <strong>Analytics:</strong> used to count visits and understand
          reading patterns in aggregate. IP addresses are anonymised.
        </li>
        <li>
          <strong>Advertising:</strong> set by Google AdSense and third-party ad vendors to serve relevant advertisements based on reading activity and measure ad effectiveness.
        </li>
      </ul>
      <p>
        You can control cookies through your browser settings, including
        clearing existing cookies and blocking future ones. Blocking essential
        cookies may cause parts of the Site to work incorrectly.
      </p>

      <h2>5. Advertising and Google AdSense</h2>
      <p>
        Acadly uses <strong>Google AdSense</strong> to display advertisements and maintain free access for all readers. In compliance with Google AdSense Policies, please review the following disclosures:
      </p>
      <ul>
        <li>
          Third-party vendors, including Google, use cookies to serve ads based on a user&apos;s prior visits to Acadly or other websites on the Internet.
        </li>
        <li>
          Google&apos;s use of advertising cookies enables it and its partners to serve ads to our readers based on their visit to Acadly and/or other sites on the Internet.
        </li>
        <li>
          Users may opt out of personalized advertising by visiting{" "}
          <a
            href="https://www.google.com/settings/ads"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-grade"
          >
            Google Ads Settings
          </a>
          . Alternatively, users can opt out of a third-party vendor&apos;s use of cookies for personalized advertising by visiting{" "}
          <a
            href="https://www.aboutads.info/choices/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-grade"
          >
            aboutads.info
          </a>
          .
        </li>
      </ul>

      <h2>6. Consent for readers in the EEA, United Kingdom and Switzerland</h2>
      <p>
        For readers in the European Economic Area (EEA), the United Kingdom, and Switzerland, Google AdSense uses an IAB TCF-compliant Consent Management Platform (CMP). When you access the Site from these regions, a consent banner allows you to explicitly accept or customize your choices regarding advertising cookies and data processing by Google and its ad partners under the EU GDPR, UK PECR, and Swiss FADP.
      </p>

      <h2>7. Analytics</h2>
      <p>
        The Site uses <strong>Google Analytics 4</strong> to understand
        aggregate use of the Site, such as which articles are read and which
        device categories are common. Google Analytics 4 does not log or
        store full IP addresses; IP data is used only transiently to derive
        approximate location and is then discarded by Google. Analytics data
        is used in aggregate and is not used to identify individual readers.
        If you prefer not to be measured, most browsers offer a &quot;Do Not
        Track&quot; setting and there are browser extensions that block
        Google Analytics entirely.
      </p>

      <h2>8. Sharing of information</h2>
      <p>
        We share personal data only in the limited situations set out below:
      </p>
      <ul>
        <li>
          <strong>Service providers</strong> that help operate the Site, such
          as our hosting, email delivery and analytics providers. These
          providers process data on our behalf under confidentiality
          obligations.
        </li>
        <li>
          <strong>Advertising partners</strong> in the manner described in
          Section 5.
        </li>
        <li>
          <strong>Authorities or professional advisers</strong> where we are
          legally required to disclose information, or where doing so is
          necessary to protect the rights, safety or property of Acadly or
          its readers.
        </li>
      </ul>
      <p>
        Some of these providers may be located outside your country of
        residence. In every case we take reasonable steps to ensure your
        information is protected in line with this policy.
      </p>

      <h2>9. Data retention</h2>
      <p>
        Email correspondence is kept for as long as it is useful to answer your
        question and to keep a record of the exchange, and is deleted on
        request. Email addresses used for updates are retained until you
        unsubscribe. Server access logs are retained for a short window,
        typically no longer than ninety days, and are then rotated out.
        Aggregated analytics data may be kept for longer periods because it
        does not identify individuals.
      </p>

      <h2>10. Your rights</h2>
      <p>
        Depending on where you live, you may have the right to request access
        to the personal data we hold about you, to have it corrected or
        deleted, to object to certain uses or to receive a copy in a portable
        format. To exercise any of these rights, email{" "}
        <a href="mailto:hello@acadly.in">hello@acadly.in</a> from the same
        address you used to write to us, so we can verify the request.
      </p>
      <p>
        Readers in India can raise concerns under the Digital Personal Data
        Protection Act, 2023. Readers in the European Economic Area and the
        United Kingdom retain their rights under the General Data Protection
        Regulation and the UK GDPR and may lodge a complaint with their local
        supervisory authority. Readers in California and other US states with
        similar laws have parallel rights under their state statutes.
      </p>

      <h2>11. Children</h2>
      <p>
        The Site is written for a college-age audience. Acadly does not
        knowingly collect personal information from children under the age of
        thirteen. If you believe a child has provided personal information to
        us, please write to{" "}
        <a href="mailto:hello@acadly.in">hello@acadly.in</a> and we will
        delete it promptly.
      </p>

      <h2>12. Security</h2>
      <p>
        The Site is served over HTTPS. We take reasonable technical and
        organisational measures to protect the limited information we handle,
        but no method of transmission or storage on the internet is completely
        secure. Please avoid sending sensitive personal information over
        email.
      </p>

      <h2>13. Third-party links</h2>
      <p>
        Articles on the Site link to external websites for reference and
        further reading. Those websites have their own privacy practices,
        which we do not control and are not responsible for. Please review
        their policies before providing any information to them.
      </p>

      <h2>14. Changes to this policy</h2>
      <p>
        This policy may be updated from time to time to reflect changes in the
        Site, in the services we use, or in the law. When changes are made,
        the &quot;Last updated&quot; date at the top of this page will be
        revised. Significant changes will be highlighted on the Site for a
        reasonable period after they take effect.
      </p>

      <h2>15. How to contact us</h2>
      <p>
        For any question about this policy or your personal data, write to{" "}
        <a href="mailto:hello@acadly.in">hello@acadly.in</a>. For general
        enquiries, use the <a href="/contact">contact page</a> or write to{" "}
        <a href="mailto:hello@acadly.in">hello@acadly.in</a>.
      </p>
    </div>
  );
}